// OCEL Domain API - Maps to backend/src/ocel/
// Contains OCEL file management and data operations

import { apiClient, makeRequest, makePostRequest, fetchHighPriority } from './client';

/**
 * Get list of available OCEL files
 * @returns {Promise<Array>} List of available files
 */
export const getAvailableFiles = async () => {
  return await makeRequest('/ocel/files', {}, 'get available files');
};

/**
 * Get the currently active file type
 * @param {string} [customerId] - If given, prefer this ETL customer's own
 *   latest-activated file over the process-wide default (see backend
 *   file_manager service — falls back to the global default if this
 *   customer has none on record yet).
 * @returns {Promise<object>} Object containing active_file_type
 */
export const getActiveFileType = async (customerId = null) => {
  const config = customerId ? { params: { customer_id: customerId } } : {};
  return await makeRequest('/ocel/active-file', config, 'get active file type');
};

/**
 * Set the active file type
 * @param {string} fileType - The file type to set as active
 * @returns {Promise<object>} Success response
 */
export const setActiveFileType = async (fileType) => {
  return await makePostRequest('/ocel/active-file', { file_type: fileType }, {}, 'set active file type');
};

/**
 * Upload a new OCEL file
 * @param {File} file - The file to upload
 * @param {string} fileType - Optional custom file type identifier
 * @returns {Promise<object>} Upload result
 */
export const uploadOCELFile = async (file, fileType = null, displayName = null, customerId = null) => {
  const formData = new FormData();
  formData.append('file', file);

  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 300000, // 5 minutes for file upload
  };

  // URLSearchParams already percent-encodes each value when serialized via
  // .toString() below — wrapping the values in encodeURIComponent() first
  // double-encodes them (e.g. a space survives as the literal text "%20"
  // instead of becoming a real space), which is exactly why a display name
  // with a space or umlaut never came through as typed.
  const params = new URLSearchParams();
  if (fileType) params.append('file_type', fileType);
  if (customerId) params.append('customer_id', customerId);
  if (displayName && displayName.trim()) params.append('display_name', displayName.trim());
  const qs = params.toString();
  const url = qs ? `/ocel/upload?${qs}` : '/ocel/upload';

  console.time('file upload time');
  try {
    const response = await apiClient.post(url, formData, config);
    console.timeEnd('file upload time');
    console.log('Successfully uploaded OCEL file:', response.data);
    return response.data;
  } catch (error) {
    console.timeEnd('file upload time');
    console.error('Error uploading OCEL file:', error.response || error.message || error);

    const errorMsg = error.response?.data?.detail || error.message || 'File upload failed';
    throw new Error(`Failed to upload OCEL file: ${errorMsg}`);
  }
};

/**
 * Delete an OCEL file
 * @param {string} fileType - The file type to delete
 * @returns {Promise<object>} Deletion result
 */
export const deleteOCELFile = async (fileType) => {
  console.time('file deletion time');
  try {
    const response = await apiClient.delete(`/ocel/files/${fileType}`);
    console.timeEnd('file deletion time');
    console.log('Successfully deleted OCEL file:', fileType);
    return response.data;
  } catch (error) {
    console.timeEnd('file deletion time');
    console.error('Error deleting OCEL file:', error.response || error.message || error);

    const errorMsg = error.response?.data?.detail || error.message || 'File deletion failed';
    throw new Error(`Failed to delete OCEL file: ${errorMsg}`);
  }
};

/**
 * Download an OCEL file's raw JSON, triggering a browser save dialog.
 * @param {string} fileType - The file type to download
 * @param {string} [displayName] - Fallback filename if the server doesn't send one
 */
export const downloadOCELFile = async (fileType, displayName = null) => {
  if (!fileType) {
    throw new Error('downloadOCELFile: fileType is required');
  }
  try {
    const response = await apiClient.get(
      `/ocel/files/${encodeURIComponent(fileType)}/download`,
      { responseType: 'blob', timeout: 600000 }
    );

    const disposition = response.headers['content-disposition'] || '';
    const match = disposition.match(/filename="?([^";]+)"?/i);
    const filename = match?.[1] || `${displayName || fileType}.json`;

    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    let detail = error.message;
    if (error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        detail = JSON.parse(text)?.detail || text;
      } catch (_) {
        // leave detail as error.message
      }
    } else {
      detail = error.response?.data?.detail || error.message;
    }
    throw new Error(`Failed to download OCEL file: ${detail}`);
  }
};

/**
 * Get metadata for an OCEL file. Gates the initial "Loading dataset" screen
 * alongside getResourceValues, so this uses fetchHighPriority (native fetch +
 * Chrome's priority hint, plus its own in-flight coalescing) instead of
 * makeRequest/axios — on initial mount ~20 tiles fire simultaneously and can
 * saturate the browser's ~6 concurrent HTTP/1.1 connections per origin;
 * without prioritization this request can end up queued behind them even
 * though its own cost is trivial once it reaches the server.
 * @param {string} fileType - The file type to query (required — omitting it
 *   would resolve to the server-global active file, which belongs to other users)
 * @returns {Promise<object>} File metadata
 */
export const getOCELMetadata = async (fileType) => {
  if (!fileType) {
    throw new Error('getOCELMetadata: fileType is required');
  }
  return await fetchHighPriority('/ocel/metadata', { file_type: fileType }, 'get OCEL metadata');
};

/**
 * Get information about a specific file
 * @param {string} fileType - The file type to get info for
 * @returns {Promise<object>} File information
 */
export const getFileInfo = async (fileType) => {
  return await makeRequest(`/ocel/files/${fileType}/info`, {}, 'get file info');
};

/**
 * Get the cache warm-up status for a file.
 * @param {string} fileType - The file type to query (required)
 * @returns {Promise<{status: 'running'|'completed'|'unknown', started_at?: string, finished_at?: string, targets?: number, skipped?: number}>}
 */
export const getWarmupStatus = async (fileType) => {
  if (!fileType) {
    throw new Error('getWarmupStatus: fileType is required');
  }
  return await makeRequest(`/ocel/files/${encodeURIComponent(fileType)}/warm-status`, {}, 'get warm-up status');
};

/**
 * Get per-tile cache warm-up status for the current user's own dashboard on a file.
 * @param {string} fileType - The file type to query (required)
 * @returns {Promise<object>} { file_type, config_saved, not_applicable_reason, pass_status, started_at, finished_at, progress, tiles: [...] }
 */
export const getMyWarmupStatus = async (fileType) => {
  if (!fileType) {
    throw new Error('getMyWarmupStatus: fileType is required');
  }
  return await makeRequest(`/ocel/files/${encodeURIComponent(fileType)}/warm-status/mine`, {}, 'get my warm-up status');
};

// DEBUG(temp): remove alongside WarmupDebugPanel.jsx once cache warming is fixed.
/**
 * Get unredacted cache warm-up status for every known file at once, across all
 * users/configs/tiles. Temporary diagnostic tool — no per-file scoping.
 * @returns {Promise<{files: Record<string, object>}>}
 */
export const getAllFilesWarmupStatusDebug = async () => {
  return await makeRequest('/ocel/debug/warm-status-all', {}, 'get debug warm-up status (all files)');
};

/**
 * Get combined counts of events and objects
 * @param {string} fileType - The file type to query (required)
 * @returns {Promise<{totalEvents:number,totalObjects:number}>}
 */
export const getCounts = async (fileType) => {
  if (!fileType) {
    throw new Error('getCounts: fileType is required');
  }
  return await makeRequest('/counts', { params: { file_type: fileType } }, 'get counts');
};

/**
 * Get available resource values for a file. Gates the initial "Loading
 * dataset" screen alongside getOCELMetadata — see that function's comment for
 * why this uses fetchHighPriority instead of makeRequest/axios.
 * @param {string} fileType - The file type to query
 * @returns {Promise<{values: string[], used_column: string}>}
 */
export const getResourceValues = async (fileType) => {
  return await fetchHighPriority('/ocel/resource-values', { file_type: fileType }, 'get resource values');
};

/**
 * Get the last calendar day with at least one event for a specific set of
 * resources — scopes ResourceStatusMonitor's "Letzter aktiver Tag" preset to
 * the tile's own configured resources instead of the whole dataset. Like
 * getOCELMetadata, deliberately NOT routed through useGlobalAPI: this is a
 * date-picker helper, not filtered analytics, and global filters silently
 * reshaping "which day counts as last active" would be surprising.
 * @param {string} fileType - The file type to query (required)
 * @param {string[]} resources - Resource values to scope the lookup to
 * @param {string} [resourceColumn] - Explicit resource column; auto-detected if omitted
 * @returns {Promise<{last_active_date: string|null, used_column: string|null}>}
 */
export const getResourceLastActiveDate = async (fileType, resources, resourceColumn) => {
  return await makePostRequest(
    '/ocel/resource-last-active-date',
    { file_type: fileType, resources, resource_column: resourceColumn },
    {},
    'get resource last active date',
  );
};

/**
 * Update the display name of an OCEL file
 * @param {string} fileType - The file type identifier (immutable)
 * @param {string} displayName - New display name (max 100 chars)
 * @returns {Promise<object>} Updated file info
 */
export const updateFileDisplayName = async (fileType, displayName) => {
  try {
    const response = await apiClient.patch(
      `/ocel/files/${encodeURIComponent(fileType)}`,
      { display_name: displayName }
    );
    return response.data;
  } catch (error) {
    const errorMsg = error.response?.data?.detail || error.message || 'Rename failed';
    throw new Error(`Failed to update display name: ${errorMsg}`);
  }
};

/**
 * Clear OCEL cache
 * @param {string} fileType - Optional file type to clear (clears all if not specified)
 * @returns {Promise<object>} Cache clear result
 */
export const clearOCELCache = async (fileType = null) => {
  const params = fileType ? { file_type: fileType } : {};
  return await makePostRequest('/ocel/cache/clear', null, { params }, 'clear OCEL cache');
};

/**
 * Set which ETL customers may see a file (admin only).
 * @param {string} fileType - The file to scope
 * @param {string[]|null} allowedCustomers - customer IDs, ['*'] for everyone,
 *   or null to clear the override (fall back to the file's origin customer)
 * @returns {Promise<object>} Updated file info (includes allowed_customers)
 */
export const setFileAccess = async (fileType, allowedCustomers) => {
  try {
    const response = await apiClient.put(
      `/ocel/files/${encodeURIComponent(fileType)}/access`,
      { allowed_customers: allowedCustomers },
    );
    return response.data;
  } catch (error) {
    const errorMsg = error.response?.data?.detail || error.message || 'Failed to set access';
    throw new Error(`Failed to set file access: ${errorMsg}`);
  }
};

/**
 * List configured ETL customers (used to populate the access multi-select).
 * Scoped to the caller on the backend; admins (unrestricted) get them all.
 * @returns {Promise<Array<{customer_id:string, display_name:string}>>}
 */
export const getETLCustomers = async () => {
  const data = await makeRequest('/etl-customers', {}, 'get ETL customers');
  return data?.customers || [];
};
